/**
 * Owns every piece of state + side-effect for /reconcile. Returns a flat
 * bag of `{ state, derived, setters, handlers }` that the shell + views
 * consume.
 *
 * Logic preserved 1:1 from the original 674-line Reconciliation.js:
 *   - fetch /accounts + /reconcile/items?year=Y&month=M on mount + period change
 *   - tag every row (computeTag), filter by tag + account, sort by column
 *   - row-level action: reconcile / unreconcile / confirm-estimate / materialise-projection
 *   - bulk reconcile: materialise any projections in the selection, then
 *     POST /api/transactions/bulk-reconcile for the resulting txn_ids
 *   - month navigation (prev / next arrows, dropdown picks)
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { computeTag } from '../../lib/reconcileTags';
import { fmtMoney } from '../../lib/format';
import { toUIType } from '../../lib/liabilityType';
import { todayIso } from './constants';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function useReconcile() {
  const [accounts, setAccounts] = useState([]);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [autoReconcileOpen, setAutoReconcileOpen] = useState(false);
  const [autoReconcileUpsellOpen, setAutoReconcileUpsellOpen] = useState(false);

  const today = new Date();
  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth() + 1);

  const [tagFilter, setTagFilter] = useState('all');
  const [accountFilter, setAccountFilter] = useState('all');
  const [selectedKeys, setSelectedKeys] = useState(new Set());
  const [busy, setBusy] = useState(false);
  const [sortConfig, setSortConfig] = useState({ column: 'date', direction: 'asc' });

  const [confirmTarget, setConfirmTarget] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [accountsRes, itemsRes] = await Promise.all([
        axios.get(`${API}/accounts`, { withCredentials: true }),
        axios.get(`${API}/reconcile/items?year=${year}&month=${month}`, { withCredentials: true }),
      ]);
      setAccounts(accountsRes.data);
      setItems(itemsRes.data);
      setSelectedKeys(new Set());
    } catch (error) {
      toast.error('Failed to load reconciliation data');
    } finally {
      setLoading(false);
    }
  }, [year, month]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const accountNameMap = useMemo(
    () => Object.fromEntries(accounts.map((a) => [a.account_id, a.name])),
    [accounts],
  );
  // Map for the liability-aware Income/Expense swap on row colours + amount sign.
  const accountKindMap = useMemo(
    () => Object.fromEntries(accounts.map((a) => [a.account_id, a.kind || 'asset'])),
    [accounts],
  );

  // Build tagged rows
  const rows = useMemo(() => {
    const tIso = todayIso();
    const tagged = items.map((it, idx) => {
      const key = it.kind === 'projection'
        ? `rec-${it.recurring_id}-${it.date}`
        : `tx-${it.transaction_id || idx}`;
      return {
        ...it,
        key,
        tag: computeTag(it, tIso),
      };
    });

    let filtered = tagged;
    if (tagFilter !== 'all') filtered = filtered.filter((r) => r.tag === tagFilter);
    if (accountFilter !== 'all') filtered = filtered.filter((r) => r.account_id === accountFilter);

    const dir = sortConfig.direction === 'asc' ? 1 : -1;
    const valueFor = (item) => {
      switch (sortConfig.column) {
        case 'description': return (item.description || '').toLowerCase();
        case 'tag': return item.tag || '';
        case 'account': return (accountNameMap[item.account_id] || '').toLowerCase();
        case 'amount': {
          const uiType = toUIType(item.transaction_type, accountKindMap[item.account_id]);
          return (uiType === 'income' ? 1 : -1) * item.amount;
        }
        case 'date':
        default:
          return item.date || '';
      }
    };
    filtered.sort((a, b) => {
      const va = valueFor(a), vb = valueFor(b);
      if (va < vb) return -1 * dir;
      if (va > vb) return 1 * dir;
      // Tiebreaker: date so equal sort keys still feel chronological
      return (a.date < b.date ? -1 : 1);
    });
    return filtered;
  }, [items, tagFilter, accountFilter, sortConfig, accountNameMap, accountKindMap]);

  const toggleSort = (column) => {
    setSortConfig((prev) => {
      if (prev.column === column) {
        return { column, direction: prev.direction === 'asc' ? 'desc' : 'asc' };
      }
      return { column, direction: column === 'date' || column === 'amount' ? 'desc' : 'asc' };
    });
  };

  // Bulk-eligible keys: anything non-estimate and NOT already in the
  // wrong state for the bulk action. Projections are bulk-eligible
  // because the bulk path materialises them first.
  const bulkEligibleKeys = useMemo(
    () => rows.filter((r) => !r.is_estimated && !r.reconciled).map((r) => r.key),
    [rows],
  );

  const toggleSelect = (key) => {
    setSelectedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedKeys.size === bulkEligibleKeys.length && bulkEligibleKeys.length > 0) {
      setSelectedKeys(new Set());
    } else {
      setSelectedKeys(new Set(bulkEligibleKeys));
    }
  };

  // -- API helpers ---------------------------------------------------------
  const reconcileTransactionIds = async (ids, reconciled = true) => {
    if (!ids.length) return;
    await axios.post(
      `${API}/transactions/bulk-reconcile`,
      { transaction_ids: ids, reconciled },
      { withCredentials: true },
    );
  };

  const finalizeProjection = async (row, { amount, description, category }) => {
    const res = await axios.post(
      `${API}/recurring/${row.recurring_id}/finalize`,
      {
        date: row.date,
        actual_amount: amount,
        description: description || null,
        category: category || null,
      },
      { withCredentials: true },
    );
    return res.data.transaction_id;
  };

  // -- Single row actions --------------------------------------------------
  const handleRowAction = async (row) => {
    if (busy) return;
    // Reconciled rows: action is Unreconcile (only real txns can be unreconciled).
    if (row.reconciled && row.kind === 'transaction') {
      setBusy(true);
      try {
        await reconcileTransactionIds([row.transaction_id], false);
        toast.success(`Unreconciled "${row.description}"`);
        await fetchData();
      } catch (err) {
        toast.error('Failed to unreconcile');
      } finally {
        setBusy(false);
      }
      return;
    }
    // Estimate: confirm amount dialog
    if (row.is_estimated) {
      setConfirmTarget(row);
      return;
    }
    // Non-estimate, not reconciled: tick → reconcile (materialise first if projection)
    setBusy(true);
    try {
      if (row.kind === 'transaction') {
        await reconcileTransactionIds([row.transaction_id]);
      } else {
        const newTxnId = await finalizeProjection(row, {
          amount: row.amount,
          description: row.description,
          category: row.category,
        });
        await reconcileTransactionIds([newTxnId]);
      }
      toast.success(`Reconciled £${fmtMoney(row.amount)} ${row.description}`);
      await fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to reconcile');
    } finally {
      setBusy(false);
    }
  };

  const handleConfirmAmount = async (values) => {
    const row = confirmTarget;
    if (!row) return;
    try {
      if (row.kind === 'transaction') {
        await axios.put(
          `${API}/transactions/${row.transaction_id}`,
          {
            account_id: row.account_id,
            date: row.date,
            description: values.description,
            amount: values.amount,
            transaction_type: row.transaction_type,
            category: values.category,
            is_estimated: false,
          },
          { withCredentials: true },
        );
        await reconcileTransactionIds([row.transaction_id]);
      } else {
        const newTxnId = await finalizeProjection(row, values);
        await reconcileTransactionIds([newTxnId]);
      }
      toast.success(`Reconciled £${fmtMoney(values.amount)} ${values.description}`);
      setConfirmTarget(null);
      await fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to reconcile');
      throw err;
    }
  };

  // -- Bulk reconcile: materialise projections first (in parallel — was
  // sequential N+1; now a single Promise.all), then bulk-reconcile.
  const bulkReconcile = async () => {
    const targetKeys = Array.from(selectedKeys);
    if (targetKeys.length === 0) {
      toast.error('Select items first');
      return;
    }
    const targets = rows.filter((r) => targetKeys.includes(r.key));
    setBusy(true);
    try {
      // Split projections from already-real transactions. Projections
      // need a finalize round-trip first; real txns can go straight into
      // the bulk-reconcile payload.
      const projections = targets.filter((r) => r.kind === 'projection');
      const realTxnIds = targets
        .filter((r) => r.kind !== 'projection' && r.transaction_id)
        .map((r) => r.transaction_id);

      // Parallelise the materialisation hops so 50+ scheduled rows don't
      // burn 50+ sequential round trips.
      const materialisedIds = await Promise.all(
        projections.map((row) => finalizeProjection(row, {
          amount: row.amount,
          description: row.description,
          category: row.category,
        })),
      );

      const idsToReconcile = [...realTxnIds, ...materialisedIds];
      if (idsToReconcile.length > 0) {
        await reconcileTransactionIds(idsToReconcile, true);
      }
      toast.success(`Reconciled ${targets.length} item${targets.length === 1 ? '' : 's'}`);
      await fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Bulk reconcile failed');
    } finally {
      setBusy(false);
    }
  };

  // -- Month navigation ----------------------------------------------------
  const stepMonth = (delta) => {
    let m = month + delta;
    let y = year;
    if (m < 1) { m = 12; y -= 1; }
    if (m > 12) { m = 1; y += 1; }
    setMonth(m);
    setYear(y);
  };

  return {
    // flags
    loading,
    busy,
    // state
    accounts,
    rows,
    year,
    month,
    tagFilter,
    accountFilter,
    selectedKeys,
    sortConfig,
    confirmTarget,
    autoReconcileOpen,
    autoReconcileUpsellOpen,
    // derived
    accountNameMap,
    accountKindMap,
    bulkEligibleKeys,
    // setters
    setYear,
    setMonth,
    setTagFilter,
    setAccountFilter,
    setConfirmTarget,
    setAutoReconcileOpen,
    setAutoReconcileUpsellOpen,
    // handlers
    fetchData,
    stepMonth,
    toggleSort,
    toggleSelect,
    toggleSelectAll,
    handleRowAction,
    handleConfirmAmount,
    bulkReconcile,
  };
}
